// src/components/upmode-agent/upmode-agent-page.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  MessageSquare, Plus, Trash2, Send, Bot, Settings, Loader2,
  Search, ChevronRight, AlertCircle, Globe, Lock, PanelLeft, X, Rocket,
} from "lucide-react";
import { toast } from "sonner";
import {
  upmodeAgentApi, streamChat,
  UpmodeAgent, UpmodeConversationSummary, UpmodeMessage,
} from "@/app/services/upmode-agent/upmode-agent-api";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { ChatMessage } from "./chat-message";
import { EmailConfirmCard, PendingEmail } from "./email-confirm-card";
import { PublishConfirmCard, PendingPublish } from "./publish-confirm-card";
import { AgentManagerModal } from "./agent-manager-modal";
import { ConfirmDialog } from "../ui/confirm-dialog";
import { useResponsive } from "@/app/hooks/common/use-breakpoint";

interface UpmodeAgentPageProps {
  activeBrandName?: string;
  isSidebarCollapsed?: boolean;
  onSidebarToggle?: (c: boolean) => void;
}

export function UpmodeAgentPage({ activeBrandName }: UpmodeAgentPageProps) {
  const [agents, setAgents] = useState<UpmodeAgent[]>([]);
  const [conversations, setConversations] = useState<UpmodeConversationSummary[]>([]);
  const [activeConvId, setActiveConvId] = useState<number | null>(null);
  const [activeConv, setActiveConv] = useState<UpmodeConversationSummary | null>(null);
  const [messages, setMessages] = useState<UpmodeMessage[]>([]);
  const [streamingText, setStreamingText] = useState("");
  const [toolEvents, setToolEvents] = useState<
    Array<{ type: "tool_start" | "tool_result"; name: string; id: string; result?: any }>
  >([]);
  const [pendingEmail, setPendingEmail] = useState<PendingEmail | null>(null);

  // NEW: pending publish confirmation card
  const [pendingPublish, setPendingPublish] = useState<PendingPublish | null>(null);
  const [publishing, setPublishing] = useState(false);

  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showAgentManager, setShowAgentManager] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [convPendingDelete, setConvPendingDelete] = useState<UpmodeConversationSummary | null>(null);

  const { isDesktop } = useResponsive();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  // ── Load data ────────────────────────────────────────────────────────────

  const loadAll = useCallback(async () => {
    setIsLoading(true); setError(null);
    try {
      const [agentList, convList] = await Promise.all([
        upmodeAgentApi.listAgents(activeBrandName),
        upmodeAgentApi.listConversations(100),
      ]);
      setAgents(agentList);
      setConversations(convList);
    } catch (e: any) {
      setError(e.message || "Failed to load");
    } finally {
      setIsLoading(false);
    }
  }, [activeBrandName]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    if (!activeConvId) {
      setMessages([]); setActiveConv(null); return;
    }
    upmodeAgentApi.getConversation(activeConvId).then((detail) => {
      setActiveConv(detail.conversation);
      setMessages(detail.messages);
      setStreamingText("");
      setToolEvents([]);
      setPendingEmail(null);
      setPendingPublish(null);
    }).catch((e) => toast.error(e.message));
  }, [activeConvId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streamingText, toolEvents, pendingEmail, pendingPublish]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 160) + "px";
  }, [input]);

  useEffect(() => {
    if (isDesktop) setDrawerOpen(false);
  }, [isDesktop]);

  useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [drawerOpen]);

  // ── Start a new conversation ─────────────────────────────────────────────

  const startConversation = async (agentId: number | null) => {
    setDrawerOpen(false);
    try {
      const agent = agentId ? agents.find(a => a.id === agentId) ?? null : null;
      const conv = await upmodeAgentApi.createConversation({
        agent_id: agentId,
        brand_name: activeBrandName || null,
        title: "New conversation",
      });
      setConversations([conv, ...conversations]);
      setActiveConvId(conv.id);
      if (agent) toast.success(`Started chat with ${agent.name}`);
    } catch (e: any) {
      toast.error(e.message || "Failed to create conversation");
    }
  };

  const requestDeleteConversation = (conv: UpmodeConversationSummary) => {
    setConvPendingDelete(conv);
  };

  const confirmDeleteConversation = async () => {
    const conv = convPendingDelete;
    if (!conv) return;
    try {
      await upmodeAgentApi.deleteConversation(conv.id);
      setConversations(prev => prev.filter(c => c.id !== conv.id));
      if (activeConvId === conv.id) setActiveConvId(null);
      toast.success("Conversation deleted");
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setConvPendingDelete(null);
    }
  };

  // ── Send message ─────────────────────────────────────────────────────────

  const handleSend = async () => {
    const text = input.trim();
    if (!text || !activeConvId || isStreaming) return;

    setInput("");
    setIsStreaming(true);
    setStreamingText("");
    setToolEvents([]);
    setPendingEmail(null);
    setPendingPublish(null);

    const optimistic: UpmodeMessage = {
      id: -Date.now(),
      role: "user",
      content: text,
      tool_calls: [], tool_results: [], metadata: {},
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, optimistic]);

    const controller = new AbortController();
    abortRef.current = controller;

    let assistantText = "";
    const localToolEvents: typeof toolEvents = [];

    try {
      await streamChat(
        activeConvId,
        text,
        (evt) => {
          if (evt.type === "text") {
            assistantText += evt.delta;
            setStreamingText(assistantText);
          } else if (evt.type === "tool_use_start") {
            localToolEvents.push({ type: "tool_start", name: evt.name, id: evt.id });
            setToolEvents([...localToolEvents]);
          } else if (evt.type === "tool_result") {
            localToolEvents.push({ type: "tool_result", name: evt.name, id: evt.id, result: evt.result });
            setToolEvents([...localToolEvents]);
          } else if (evt.type === "email_confirmation") {
            setPendingEmail({
              tool_use_id: evt.tool_use_id,
              to_email: evt.to_email,
              subject: evt.subject,
              body: evt.body,
            });
          } else if (evt.type === "publish_confirmation") {
            // NEW — agent asked to publish, render the approval card
            setPendingPublish({
              tool_use_id: evt.tool_use_id,
              destinations: evt.destinations,
              content: evt.content,
              summary: evt.summary,
            });
          } else if (evt.type === "error") {
            toast.error(evt.message);
          }
        },
        controller.signal,
      );

      const detail = await upmodeAgentApi.getConversation(activeConvId);
      setMessages(detail.messages);
      setStreamingText("");
      setToolEvents([]);

      upmodeAgentApi.listConversations(100).then(setConversations).catch(() => {});
    } catch (e: any) {
      if (e.name !== "AbortError") {
        toast.error(e.message || "Chat failed");
        setError(e.message || "Chat failed");
      }
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
    }
  };

  const handleConfirmEmail = async (email: PendingEmail, edited?: Partial<PendingEmail>) => {
    if (!activeConvId) return;
    const payload = {
      conversation_id: activeConvId,
      to_email: edited?.to_email ?? email.to_email,
      subject: edited?.subject ?? email.subject,
      body: edited?.body ?? email.body,
    };
    try {
      const res = await upmodeAgentApi.sendEmail(payload);
      if (res.status === "sent") {
        toast.success(`Email sent to ${payload.to_email}`);
      } else if (res.status === "not_configured") {
        toast.warning("SMTP is not configured — email not sent.");
      } else {
        toast.error(`Email failed: ${res.error || res.status}`);
      }
      setPendingEmail(null);
      const detail = await upmodeAgentApi.getConversation(activeConvId);
      setMessages(detail.messages);
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const handleCancelEmail = () => {
    setPendingEmail(null);
    toast.info("Email cancelled");
  };

  // ── NEW: publish confirmation handlers ─────────────────────────────────

  const handleConfirmPublish = async (
    selected: Array<{ destination: any; content: Record<string, any> }>,
  ) => {
    if (!activeConvId || !pendingPublish) return;
    setPublishing(true);
    try {
      const res = await upmodeAgentApi.confirmPublish({
        conversation_id: activeConvId,
        tool_use_id: pendingPublish.tool_use_id,
        destinations: selected,
      });

      const publishedCount = res.results.filter(r => r.status === "published").length;
      const failedCount    = res.results.filter(r => r.status !== "published").length;

      if (res.status === "published") {
        toast.success(`Published to ${publishedCount} destination${publishedCount === 1 ? "" : "s"}`);
      } else if (res.status === "partially_published") {
        toast.warning(`Published to ${publishedCount}, ${failedCount} failed`);
      } else {
        toast.error("Publishing failed for all destinations");
      }

      setPendingPublish(null);
      const detail = await upmodeAgentApi.getConversation(activeConvId);
      setMessages(detail.messages);
    } catch (e: any) {
      toast.error(e.message || "Publishing failed");
    } finally {
      setPublishing(false);
    }
  };

  const handleCancelPublish = () => {
    setPendingPublish(null);
    toast.info("Publish cancelled");
  };

  const filteredConvs = useMemo(() => {
    if (!searchQuery) return conversations;
    const q = searchQuery.toLowerCase();
    return conversations.filter(c =>
      c.title.toLowerCase().includes(q) ||
      (c.brand_name || "").toLowerCase().includes(q)
    );
  }, [conversations, searchQuery]);

  const activeAgent = activeConv?.agent_id ? agents.find(a => a.id === activeConv.agent_id) ?? null : null;
  const hasWebSearch = !!activeAgent?.tools_enabled?.web_search;
  const hasPublish = !!(activeAgent?.publish_destinations && activeAgent.publish_destinations.length > 0);

  // Layout tokens
  const chatPadX     = isDesktop ? 20 : 16;
  const messagesPadY = isDesktop ? 16 : 14;
  const composerPadY = isDesktop ? 12 : 10;

  // ── Rail ─────────────────────────────────────────────────────────────────

  const railHeader = (
    <div style={{ padding: "16px 16px 12px", borderBottom: "1px solid var(--border)", flexShrink: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <div style={{ width: 30, height: 30, borderRadius: 9, backgroundColor: "rgba(13,148,136,0.1)", border: "1px solid rgba(13,148,136,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Bot style={{ width: 16, height: 16, color: "#0D9488" }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>Upmode Agent</div>
          <div style={{ fontSize: 11, color: "var(--muted-foreground)", overflowWrap: "anywhere", wordBreak: "break-word" }}>
            {activeBrandName ? `${activeBrandName} · chat with your data` : "Chat with your data"}
          </div>
        </div>
        <button onClick={() => { setShowAgentManager(true); setDrawerOpen(false); }} title="Manage agents"
          style={{ padding: 7, borderRadius: 7, border: "1px solid var(--border)", backgroundColor: "var(--muted)", cursor: "pointer", color: "var(--muted-foreground)", flexShrink: 0 }}>
          <Settings style={{ width: 13, height: 13 }} />
        </button>
        {!isDesktop && (
          <button onClick={() => setDrawerOpen(false)} title="Close" aria-label="Close agents menu"
            style={{ padding: 7, borderRadius: 7, border: "1px solid var(--border)", backgroundColor: "var(--muted)", cursor: "pointer", color: "var(--muted-foreground)", flexShrink: 0 }}>
            <X style={{ width: 13, height: 13 }} />
          </button>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 10 }}>
        <p style={{ fontSize: 10, fontWeight: 700, color: "var(--muted-foreground)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
          Start with…
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 220, overflowY: "auto" }}>
          {agents.map(a => (
            <button key={a.id} onClick={() => startConversation(a.id)}
              style={{ display: "flex", alignItems: "center", gap: 9, padding: "7px 10px", borderRadius: 8, border: "1px solid var(--border)", backgroundColor: "var(--muted)", cursor: "pointer", textAlign: "left" }}>
              <span style={{ fontSize: 16, lineHeight: 1, flexShrink: 0 }}>{a.avatar_emoji || "🤖"}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "var(--foreground)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.name}</span>
                  {a.tools_enabled.web_search && <Globe style={{ width: 9, height: 9, color: "#7C3AED", flexShrink: 0 }} />}
                  {(a.publish_destinations || []).length > 0 && <Rocket style={{ width: 9, height: 9, color: "#10B981", flexShrink: 0 }} />}
                </div>
                <div style={{ fontSize: 10, color: "var(--muted-foreground)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {a.description}
                  {a.brand_name && <> · <span style={{ color: "#0D9488" }}>{a.brand_name}</span></>}
                </div>
              </div>
              <ChevronRight style={{ width: 12, height: 12, color: "var(--muted-foreground)", flexShrink: 0 }} />
            </button>
          ))}
          <button onClick={() => { setShowAgentManager(true); setDrawerOpen(false); }}
            style={{ display: "flex", alignItems: "center", gap: 9, padding: "7px 10px", borderRadius: 8, border: "1px dashed var(--border)", backgroundColor: "transparent", cursor: "pointer", color: "var(--muted-foreground)" }}>
            <Plus style={{ width: 12, height: 12 }} />
            <span style={{ fontSize: 12 }}>Create custom agent</span>
          </button>
        </div>
      </div>

      <div style={{ position: "relative" }}>
        <Search style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", width: 13, height: 13, color: "var(--muted-foreground)" }} />
        <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search conversations…"
          style={{ width: "100%", padding: "7px 10px 7px 30px", borderRadius: 8, border: "1px solid var(--border)", backgroundColor: "var(--muted)", fontSize: 12, outline: "none", boxSizing: "border-box", color: "var(--foreground)" }} />
      </div>
    </div>
  );

  const railList = (
    <div style={{ flex: 1, overflowY: "auto", padding: "8px 10px" }}>
      {isLoading && <div style={{ padding: 20, textAlign: "center" }}><Loader2 style={{ width: 16, height: 16, color: "#0D9488" }} className="animate-spin" /></div>}
      {!isLoading && filteredConvs.length === 0 && (
        <div style={{ padding: "30px 10px", textAlign: "center", color: "var(--muted-foreground)", fontSize: 12 }}>
          {searchQuery ? "No conversations match your search." : "No conversations yet. Pick an agent above to start."}
        </div>
      )}
      {filteredConvs.map(c => (
        <button key={c.id} onClick={() => { setActiveConvId(c.id); setDrawerOpen(false); }}
          style={{ width: "100%", display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 10px", borderRadius: 8, border: "1px solid transparent", backgroundColor: activeConvId === c.id ? "rgba(13,148,136,0.08)" : "transparent", cursor: "pointer", textAlign: "left", marginBottom: 2, borderColor: activeConvId === c.id ? "rgba(13,148,136,0.2)" : "transparent" }}>
          <MessageSquare style={{ width: 12, height: 12, color: activeConvId === c.id ? "#0D9488" : "var(--muted-foreground)", marginTop: 3, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--foreground)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {c.title}
            </div>
            <div style={{ fontSize: 10, color: "var(--muted-foreground)", display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
              <span>{c.agent_name}</span>
              {c.brand_name && (<><span>·</span><span>{c.brand_name}</span></>)}
            </div>
          </div>
          <span role="button" tabIndex={0}
            onClick={(e) => { e.stopPropagation(); requestDeleteConversation(c); }}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); requestDeleteConversation(c); } }}
            style={{ padding: 3, borderRadius: 4, border: "none", backgroundColor: "transparent", cursor: "pointer", color: "var(--muted-foreground)", opacity: 0.6, display: "inline-flex", alignItems: "center", flexShrink: 0 }}
            title="Delete">
            <Trash2 style={{ width: 11, height: 11 }} />
          </span>
        </button>
      ))}
    </div>
  );

  const rail = <>{railHeader}{railList}</>;

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: isDesktop ? "300px 1fr" : "1fr", height: "100dvh", backgroundColor: "var(--background)" }}>
        {isDesktop ? (
          <div style={{ borderRight: "1px solid var(--border)", display: "flex", flexDirection: "column", overflow: "hidden" }}>{rail}</div>
        ) : (
          <>
            {drawerOpen && <div onClick={() => setDrawerOpen(false)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 45 }} />}
            <div style={{ position: "fixed", top: 0, left: 0, height: "100dvh", width: "min(320px, 85vw)", zIndex: 46, backgroundColor: "var(--background)", borderRight: "1px solid var(--border)", display: "flex", flexDirection: "column", overflow: "hidden", transform: drawerOpen ? "translateX(0)" : "translateX(-100%)", transition: "transform 300ms ease-in-out", boxShadow: drawerOpen ? "0 0 30px rgba(0,0,0,0.15)" : "none" }} aria-hidden={!drawerOpen}>
              {rail}
            </div>
          </>
        )}

        <div style={{ display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}>
          {!isDesktop && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderBottom: "1px solid var(--border)", backgroundColor: "var(--background)", flexShrink: 0 }}>
              <button onClick={() => setDrawerOpen(true)} aria-label="Open agents and conversations" title="Agents & conversations"
                style={{ padding: 8, borderRadius: 8, border: "1px solid var(--border)", backgroundColor: "var(--muted)", cursor: "pointer", color: "var(--foreground)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <PanelLeft style={{ width: 16, height: 16 }} />
              </button>
              {activeConvId ? (
                <>
                  <span style={{ fontSize: 18, lineHeight: 1, flexShrink: 0 }}>{activeAgent?.avatar_emoji || "🤖"}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "var(--foreground)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{activeConv?.title}</div>
                    <div style={{ fontSize: 11, color: "var(--muted-foreground)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {activeAgent?.name || activeConv?.agent_name}
                      {activeConv?.brand_name && ` · ${activeConv.brand_name}`}
                    </div>
                  </div>
                </>
              ) : (
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--foreground)" }}>Upmode Agent</div>
                  <div style={{ fontSize: 11, color: "var(--muted-foreground)", overflowWrap: "anywhere", wordBreak: "break-word" }}>
                    {activeBrandName ? `${activeBrandName} · chat with your data` : "Chat with your data"}
                  </div>
                </div>
              )}
            </div>
          )}

          {!activeConvId ? (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "var(--muted-foreground)", padding: isDesktop ? 40 : 24, textAlign: "center" }}>
              <div style={{ width: 56, height: 56, borderRadius: 14, backgroundColor: "rgba(13,148,136,0.08)", border: "1px solid rgba(13,148,136,0.15)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
                <Bot style={{ width: 26, height: 26, color: "#0D9488" }} />
              </div>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: "var(--foreground)", marginBottom: 6 }}>Start a conversation</h3>
              <p style={{ fontSize: 13, textAlign: "center", maxWidth: 420, lineHeight: 1.55 }}>
                {isDesktop ? "Pick an agent from the left, or create a custom one." : "Tap the menu to pick an agent, or create a custom one."}
                {" "}
                Agents reason over your Upmode brand data by default. Enable <strong>Web search</strong> or select <strong>publish destinations</strong> when creating a custom agent to unlock more capabilities.
              </p>
              {!isDesktop && (
                <button onClick={() => setDrawerOpen(true)}
                  style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 9, border: "1px solid rgba(13,148,136,0.25)", backgroundColor: "rgba(13,148,136,0.08)", cursor: "pointer", color: "#0D9488", fontSize: 13, fontWeight: 600 }}>
                  <PanelLeft style={{ width: 14, height: 14 }} />
                  Browse agents
                </button>
              )}
            </div>
          ) : (
            <>
              {isDesktop && (
                <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 18, lineHeight: 1, flexShrink: 0 }}>{activeAgent?.avatar_emoji || "🤖"}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "var(--foreground)", overflowWrap: "anywhere", wordBreak: "break-word" }}>
                      {activeConv?.title}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--muted-foreground)", overflowWrap: "anywhere", wordBreak: "break-word" }}>
                      {activeAgent?.name || activeConv?.agent_name}
                      {activeConv?.brand_name && ` · ${activeConv.brand_name}`}
                    </div>
                  </div>
                </div>
              )}

              {/* Scope blurb */}
              <div style={{
                margin: `10px ${chatPadX}px 0`, padding: "9px 12px", borderRadius: 10,
                backgroundColor: hasPublish
                  ? "rgba(16,185,129,0.06)"
                  : hasWebSearch
                    ? "rgba(124,58,237,0.06)"
                    : "rgba(13,148,136,0.06)",
                border: `1px solid ${hasPublish ? "rgba(16,185,129,0.2)" : hasWebSearch ? "rgba(124,58,237,0.2)" : "rgba(13,148,136,0.2)"}`,
                display: "flex", alignItems: "flex-start", gap: 8,
                fontSize: 11, color: "var(--foreground)", lineHeight: 1.5, flexShrink: 0,
              }}>
                {hasPublish ? (
                  <Rocket style={{ width: 13, height: 13, color: "#10B981", flexShrink: 0, marginTop: 1 }} />
                ) : hasWebSearch ? (
                  <Globe style={{ width: 13, height: 13, color: "#7C3AED", flexShrink: 0, marginTop: 1 }} />
                ) : (
                  <Lock style={{ width: 13, height: 13, color: "#0D9488", flexShrink: 0, marginTop: 1 }} />
                )}
                <div style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}>
                  {hasPublish ? (
                    <>This agent can reason over your <strong>Upmode data</strong>{hasWebSearch && <> and <strong>the live web</strong></>}, and publish content to: <strong>{(activeAgent?.publish_destinations || []).join(", ")}</strong>. Every publish requires your approval.</>
                  ) : hasWebSearch ? (
                    <>This agent can reason over your <strong>Upmode data</strong> and <strong>the live web</strong>. Upmode data is preferred for brand questions.</>
                  ) : (
                    <>This agent reasons <strong>only over your Upmode data</strong>. Enable <strong>Web search</strong> or <strong>publishing</strong> when creating a custom agent to unlock more.</>
                  )}
                </div>
              </div>

              {/* Messages */}
              <div ref={scrollRef}
                style={{ flex: 1, overflowY: "auto", padding: `${messagesPadY}px ${chatPadX}px ${messagesPadY + 4}px`, display: "flex", flexDirection: "column", gap: 14, minHeight: 0 }}>
                {messages.filter(m => m.role !== "tool").map(m => (
                  <ChatMessage key={m.id} message={m} agentEmoji={activeAgent?.avatar_emoji} />
                ))}

                {toolEvents.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 5, padding: "6px 12px", borderRadius: 10, backgroundColor: "rgba(37,99,235,0.04)", border: "1px solid rgba(37,99,235,0.15)", alignSelf: "flex-start", maxWidth: "95%" }}>
                    {toolEvents.map((ev, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#2563EB", overflowWrap: "anywhere", wordBreak: "break-word" }}>
                        {ev.type === "tool_start"
                          ? <><Loader2 style={{ width: 10, height: 10, flexShrink: 0 }} className="animate-spin" /><span>Calling <strong>{ev.name}</strong>…</span></>
                          : <><span style={{ flexShrink: 0 }}>✓</span><span>Got result from <strong>{ev.name}</strong></span></>}
                      </div>
                    ))}
                  </div>
                )}

                {streamingText && (
                  <ChatMessage
                    message={{
                      id: -1, role: "assistant", content: streamingText,
                      tool_calls: [], tool_results: [], metadata: {},
                      created_at: new Date().toISOString(),
                    }}
                    agentEmoji={activeAgent?.avatar_emoji}
                    streaming
                  />
                )}

                {pendingEmail && (
                  <EmailConfirmCard
                    email={pendingEmail}
                    onConfirm={(edited) => handleConfirmEmail(pendingEmail, edited)}
                    onCancel={handleCancelEmail}
                  />
                )}

                {pendingPublish && (
                  <PublishConfirmCard
                    pending={pendingPublish}
                    onConfirm={handleConfirmPublish}
                    onCancel={handleCancelPublish}
                    busy={publishing}
                  />
                )}

                {isStreaming && !streamingText && toolEvents.length === 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--muted-foreground)", fontSize: 12 }}>
                    <Loader2 style={{ width: 12, height: 12 }} className="animate-spin" />
                    <span>Thinking…</span>
                  </div>
                )}
              </div>

              {/* Composer */}
              <div style={{ borderTop: "1px solid var(--border)", padding: `${composerPadY}px ${chatPadX}px ${composerPadY + 4}px`, flexShrink: 0 }}>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-end", backgroundColor: "var(--muted)", borderRadius: 12, border: "1px solid var(--border)", padding: "8px 10px" }}>
                  <textarea ref={inputRef} value={input} onChange={e => setInput(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                    placeholder={isStreaming ? "Generating…" : isDesktop ? "Ask the agent anything…  (Enter to send, Shift+Enter for newline)" : "Ask the agent anything…"}
                    rows={1} disabled={isStreaming || !!pendingEmail || !!pendingPublish}
                    style={{ flex: 1, minWidth: 0, border: "none", outline: "none", resize: "none", background: "transparent", fontSize: 13, lineHeight: 1.5, color: "var(--foreground)", fontFamily: "inherit", maxHeight: 160, padding: "4px 2px" }} />
                  <button onClick={handleSend} disabled={!input.trim() || isStreaming || !!pendingEmail || !!pendingPublish} aria-label="Send message"
                    style={{ padding: "7px 12px", borderRadius: 8, border: "none", backgroundColor: !input.trim() || isStreaming || pendingEmail || pendingPublish ? "var(--border)" : "#0D9488", color: "white", cursor: !input.trim() || isStreaming || pendingEmail || pendingPublish ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, flexShrink: 0 }}>
                    <Send style={{ width: 12, height: 12 }} />
                    {isDesktop && "Send"}
                  </button>
                </div>

                {error && (
                  <div className="mt-2">
                    <Alert variant="destructive">
                      <AlertCircle />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {showAgentManager && (
        <AgentManagerModal
          agents={agents}
          activeBrandName={activeBrandName}
          onClose={() => setShowAgentManager(false)}
          onChanged={loadAll}
        />
      )}

      <ConfirmDialog
        open={convPendingDelete !== null}
        onOpenChange={(open) => { if (!open) setConvPendingDelete(null); }}
        onConfirm={confirmDeleteConversation}
        title="Delete this conversation?"
        description={
          convPendingDelete
            ? <>This will permanently remove <strong>"{convPendingDelete.title}"</strong> and its message history. This cannot be undone.</>
            : undefined
        }
        confirmLabel="Delete"
        destructive
      />
    </>
  );
}